/**
 * LOBBERO STAYS — every word the stay guest's pages say (plan S3).
 *
 * English only (owner decision SD-8): one locale, no switcher. The words are
 * keyed so a second locale is a new block, not a hunt through the views.
 *
 * Strings marked FIXED are read verbatim by the Playwright journey; change
 * them only together with the journey.
 *
 * The ERROR block mirrors lobbero_be/src/stays/guest-errors.js — a backend
 * script (scripts/stays/check-fe-copy.js) checks the two code lists are the
 * same, so keep it a literal object with one `CODE: 'words',` per line.
 */

export type StayLocale = 'en';

const EN = {
  // Page and document titles.
  documentTitle: 'Check-in',
  loadingLabel: 'Loading your check-in',

  // States of the whole page. FIXED.
  invalid: 'This check-in link is not valid. Please contact your host.',
  limited: 'Too many attempts. Please wait a minute and try again.',
  suspended: 'Check-in is paused. Please contact your host.',
  error: 'Something went wrong. Please try again.',
  tryAgain: 'Try again',

  // Welcome. FIXED.
  welcomeTitle: 'Welcome, {first}',
  welcomeSub: 'Your stay at {listing}, {building}',
  startCheckIn: 'Start check-in',

  // Dates. FIXED.
  datesTitle: 'Your stay',
  checkInLine: 'Check-in {date}, from {time}',
  checkOutLine: 'Check-out {date}, by {time}',
  nightsGuests: '{nights} · {guests}',
  oneNight: '1 night',
  manyNights: '{n} nights',
  oneGuest: '1 guest',
  manyGuests: '{n} guests',
  zoneNote: 'Times are {city} time.',
  zoneNoteUtc: 'Times are UTC.',
  adultsLabel: 'Adults (18 or over)',
  adultsStepperNoun: 'adults (18 or over)',
  arrivalLabel: 'What time will you arrive?',
  arrivalHint: 'So the front desk knows when to expect you.',
  confirmDates: 'These are my dates',
  confirmingDates: 'Saving…',
  datesWrong: 'Something wrong? Contact your host.',

  // Party. FIXED.
  partyTitle: "Who's coming",
  partySub: 'Everyone 18 or over verifies their own ID before the stay.',
  leadRow: '{first} (you)',
  memberRow: '{first} {initial}',
  pillNotStarted: 'Not started',
  pillPending: 'ID check in progress',
  pillVerified: 'ID verified',
  pillFailed: 'ID check failed',
  toNameCount: '{n} more adults to add.',
  toNameOne: '1 more adult to add.',
  firstName: 'First name',
  lastName: 'Last name',
  addPerson: 'Add this person',
  addingPerson: 'Adding…',
  sendLink: 'Send {first} their own link',
  makingLink: 'Making the link…',
  linkFor: 'The link for {first}',
  linkOnce: 'This link is shown once. Send it to {first} now.',
  copyLink: 'Copy link',
  copyMessage: 'Copy message',
  copied: 'Copied',
  whatsapp: 'Send on WhatsApp',
  share: 'Share',
  openHere: 'Open on this phone',
  remove: 'Remove',
  removeName: 'Remove {first}',
  removing: 'Removing…',
  continueToId: 'Continue to my ID check',
  everyoneNamed: 'Name everyone in your group to continue.',

  // Consent. FIXED.
  consentTitle: 'Before you upload your ID',
  privacyVersion: 'Privacy notice {version}',
  selfieLabel: 'Also take a selfie',
  agree: 'I agree — verify my ID',
  agreeing: 'Saving…',
  notNow: 'Not now',

  // Identity. FIXED.
  identityTitle: 'Verify your ID',
  identityBody:
    'Our ID partner checks your passport or ID card. The photo is deleted after the check. We keep only your name, document number, nationality and date of birth.',
  verifyId: 'Verify my ID',
  opening: 'Opening…',
  failedTitle: "We couldn't verify your ID",
  failedBody: 'You can try again with your passport or ID card.',
  exhausted: 'Please contact your host to finish check-in.',
  pendingTitle: 'Checking your ID',
  pending: 'Your ID check is still processing. You can close this page.',

  // Waiting and done. FIXED.
  waitingTitle: 'Your ID is verified',
  waitingBody: 'Waiting for the others in your group to verify their ID.',
  doneTitle: "You're checked in online",
  doneBody: "Show your ID at the front desk of {building} when you arrive. They know you're coming.",

  // One adult's own page (/sg/:token). FIXED.
  guestTitle: 'Hi {first}',
  guestSub: 'Verify your ID for your stay at {listing}, {building}',
  guestVerified: "You're verified. You can close this page.",

  // The return page (/s/return). FIXED.
  returnTitle: 'Your check-in',
  returnNoStorage: 'Open your check-in link again to see your status.',

  // ── Plan S6: the full check-in (/s/:token/:step). FIXED unless noted. ──
  // The steps.
  stepsAria: 'Check-in steps',
  stepTrip: 'Your trip',
  stepIdentity: 'ID check',
  stepRules: 'House rules',
  stepPayment: 'Payment',
  stepDeskShare: 'Front desk',
  // Independent Stays IS7: sharing with the building's front desk (DRAFT with its text).
  deskShareTitle: "The building's front desk",
  deskShareBuilding: '{building} has a front desk that signs guests in.',
  deskShareLater: 'This place is now linked to its building. Read this so the desk can expect you.',
  // bds-2026-11-2: a confirmation of a notice, not a consent (the share is part of the booking).
  deskShareAgree: "I have read how my details are shared with the building's front desk.",
  deskShareSave: 'Confirm and continue',
  deskShareGiven: 'The front desk at {building} is expecting you.',
  deskShareStop: 'Stop sharing with the desk',
  stepDone: 'Done',
  stepLocked: 'Finish {step} first',
  stepOf: 'Step {n} of {total}: {label}',
  stepOfShort: 'Step {n} of {total} · {label}',
  // The header on every step.
  hostLine: 'Your host: {name}',
  datesLine: '{from} – {to} · {nights}',
  // Trip.
  tripTitle: 'Your trip',
  addressLine: '{building}, {address}',
  childrenLabel: 'Children (under 18)',
  childrenStepperNoun: 'children (under 18)',
  sleepsMax: 'This home sleeps {max}.',
  bookedNote: 'Your booking says {n}. Change it here if more or fewer people are coming.',
  purposeLabel: 'Purpose of your trip',
  purposeOptional: 'Purpose of your trip (optional)',
  purpose_leisure: 'Holiday',
  purpose_business: 'Business',
  purpose_family_visit: 'Visiting family',
  purpose_medical: 'Medical',
  purpose_study: 'Study',
  purpose_other: 'Other',
  // Who is coming, on the trip step (plan ISR: the lead is guest 1).
  introLine: 'About 5 minutes. Your answers are saved as you go.',
  whoTitle: 'Who is coming',
  whoHint: 'You are guest 1 and already counted. Add everyone else who is staying.',
  leadGuestRow: 'Guest 1: {first} (you)',
  leadRowNote: 'From your booking',
  guestN: 'Guest {n}',
  addAnother: 'Add another guest',
  removeGuest: 'Remove guest {n}',
  countLine: '{guests}: {adults} and {children}',
  oneAdult: '1 adult',
  manyAdults: '{n} adults',
  noChildren: 'no children',
  oneChild: '1 child',
  manyChildren: '{n} children',
  countPending: 'Add each date of birth to count adults and children.',
  atMax: 'This home sleeps {max}, you included.',
  bookedFor: 'Your booking was for {n}. That is fine if more or fewer people are coming, up to {max}.',
  dobHint: 'Used only to tell adults from children. We do not keep it.',
  draftKept: 'Your answers on this page are kept on this device until you continue.',
  fixAbove: 'Some details are missing. They are marked above.',
  rulesSummaryTitle: 'House rules in short',
  continue: 'Continue',
  saving: 'Saving…',
  // Who is coming (the identity step).
  identityStepSub: 'Every adult verifies their ID before the stay. An adult adds the details of each child.',
  identityStepSubLead: 'You verify your ID before the stay. Add everyone who is coming with you.',
  partyFormTitle: 'Everyone coming with you',
  partyFormHint: 'We use each date of birth only to tell adults from children, and we do not keep it.',
  personTitle: 'Person {n}',
  birthDateLabel: 'Date of birth',
  keepsAge: 'Already added',
  savePeople: 'Save these people',
  editPeople: 'Change who is coming',
  peopleFirst: 'Add everyone who is coming on the first step, then come back here for the ID check.',
  youRow: '{first} (you)',
  verifyPerson: 'Verify {first}',
  resumePerson: "Continue {first}'s ID check",
  addDetails: "Add {first}'s details",
  pillDeclared: 'Details added',
  pillNeedsDetails: 'Details needed',
  pillNotNeeded: 'No ID needed',
  pillInPerson: 'Checked at the desk',
  attemptsLeft: '{n} tries left',
  consentForTitle: 'Before {first} uploads an ID',
  continueToVerify: 'Continue to verification',
  relationshipLabel: 'How they are related to the adult',
  travelsWithLabel: 'Travels with',
  rel_parent: 'Parent',
  rel_legal_guardian: 'Legal guardian',
  rel_grandparent: 'Grandparent',
  rel_sibling: 'Brother or sister',
  rel_other_relative: 'Other relative',
  rel_authorised_adult: 'Adult with permission',
  rel_other: 'Other',
  chooseOne: 'Choose…',
  saveDetails: 'Save details',
  cancel: 'Cancel',
  detailsTitle: "{first}'s details",
  identityWaiting: 'Still to do: {names}.',
  linkReady: 'Their own link',
  // Independent Stays IS0 — the in-house ID check (never "verified": R-IS0.6).
  identityStepSubInhouse: 'Every adult adds their ID before the stay. An adult adds the details of each child.',
  identityStepSubLeadInhouse: 'You add your ID before the stay. Add everyone who is coming with you.',
  addIdPerson: "Add {first}'s ID",
  addIdAgain: "Scan {first}'s ID again",
  pillIdConfirmed: 'ID confirmed',
  pillIdHostCheck: 'Host will confirm',
  pillIdRescan: 'Please scan again',
  idConsentTitle: 'Before {first} adds an ID',
  idAgree: 'I agree',
  idDocTypeLabel: 'Kind of document',
  idDocPassport: 'Passport',
  idDocIdCard: 'ID card',
  idDocOther: 'Other ID',
  idTakePhoto: 'Take a photo',
  idTakePhotoAgain: 'Take another photo',
  idPhotoHint: 'Photograph the page with your photo and details, flat and in good light, with all four corners in view.',
  idPhotoPrivacy: 'Your photo is used only to read the details and is deleted straight away.',
  idReading: 'Reading your document…',
  idReadFailed: "We couldn't read that photo. Try again in good light, or enter the details yourself.",
  idReadFailedOften: "We couldn't read your photos. Please enter the details yourself.",
  idManualOnly: 'Enter the details exactly as printed on your document.',
  idEnterYourself: "Can't scan? Enter the details yourself",
  idBackToPhoto: 'Take a photo instead',
  idConfirmTitle: 'Is this right?',
  idConfirmHint: 'Check every detail against your document and correct anything that is wrong.',
  idLowConfidence: 'Some details were hard to read. Please check them carefully.',
  idFirstName: 'First name(s)',
  idLastName: 'Last name(s)',
  idDocNumber: 'Document number',
  idDocCountry: 'Issued by',
  idNationality: 'Nationality',
  idBirthDate: 'Date of birth',
  idExpiryDate: 'Expiry date',
  idChooseCountry: 'Choose a country…',
  idConfirm: 'Confirm',
  idConfirming: 'Saving…',
  idScansLeft: '{n} photos left',
  idDoneConfirmed: 'Thanks, {first}. Your ID is confirmed.',
  idDoneHostCheck: 'Thanks, {first}. Your host will confirm your ID.',
  idDoneRescan: 'Your host asked for a new photo of your ID.',
  idFixField: 'Check this detail.',
  // House rules.
  rulesTitle: 'House rules',
  rulesVersion: 'Version {n}',
  sigTypeTab: 'Type',
  sigDrawTab: 'Draw',
  sigTypeLabel: 'Type your full name',
  sigDrawHint: 'Draw your signature with your finger or mouse',
  sigClear: 'Clear',
  sigEmpty: 'Draw your signature, or type your name instead.',
  agreeRules: 'I have read the house rules and agree to follow them during my stay.',
  agreeRequired: 'Tick the box to agree to the house rules.',
  signRules: 'Sign house rules',
  signing: 'Signing…',
  signedCopy: 'A signed copy is on its way to your email.',
  downloadCopy: 'Download your copy',
  rulesSigned: 'House rules signed',
  // Payment (S10's slot).
  paymentTitle: 'Payment',
  paymentSoon: 'Payment will be available here soon.',
  // Done.
  doneCompleteTitle: 'Check-in complete',
  doneLookForward: 'We look forward to welcoming you, {first}.',
  doneArrivalDetails: 'Your door code and arrival guide appear here 30 minutes before check-in.',
  arrivedAction: "I've arrived",
  checkedOutAction: "I've checked out",
  helpAction: 'I need help',
  availableOn: 'Available on {date}',
  arrivedDone: 'Your host knows you have arrived.',
  checkedOutDone: 'Thank you. Your host knows you have checked out.',
  helpTitle: 'Ask your host for help',
  helpLabel: 'What do you need?',
  sendToHost: 'Send to host',
  sending: 'Sending…',
  hostTold: 'Your host has been told.',
  // After check-out (K-10).
  endedTitle: 'Thank you for staying with us, {first}.',
  endedBody: 'Your stay at {listing} has ended. We hope to see you again.',

  // ── Plan S7: the guest guide (/s/:token/guide[/section]). FIXED unless noted
  // (ai-plans/stays/reports/PLAN-S7-FE-CONTRACT.md §4). ──
  // The frame.
  'guide.documentTitle': 'Your stay guide',
  'guide.loading': 'Loading your guide',
  'guide.navLabel': 'Guide sections',
  'guide.navArrival': 'Arrival',
  'guide.navEssentials': 'Essentials',
  'guide.navLocal': 'Local tips',
  'guide.navCheckout': 'Check-out',
  'guide.navPhotos': 'Photos',
  'guide.backToTop': 'Back to top',
  'guide.backToGuide': 'Back to your guide',
  // The PIN gate.
  'guide.pinTitle': 'Enter your guide PIN',
  'guide.pinLabel': 'Five-digit PIN',
  'guide.pinSubmit': 'Open my guide',
  'guide.pinChecking': 'Checking…',
  'guide.pinIncomplete': 'Enter all five digits',
  'guide.pinHelper': 'Your host sent this PIN with your booking.',
  'guide.pinWrong': 'That PIN is not right. {n} tries left.',
  'guide.pinWrongOne': 'That PIN is not right. 1 try left.',
  'guide.pinFormat': 'Enter the five-digit PIN from your host.',
  'guide.pinLocked': 'Too many wrong PINs. Try again at {time}.',
  'guide.pinLockedLater': 'Too many wrong PINs. Try again later.',
  // The whole-page states.
  'guide.upcoming': 'Your guide opens on {date} at {time}.',
  'guide.ended': 'Thank you for staying with us, {first}. This guide is now closed.',
  'guide.endedNoName': 'Thank you for staying with us. This guide is now closed.',
  'guide.limited': 'Too many tries. Wait a minute and try again.',
  'guide.error': 'We could not load your guide.',
  // The hub.
  'guide.welcome': 'Welcome, {first}.',
  'guide.welcomeNoName': 'Welcome.',
  'guide.hostPhotoAlt': 'Your host, {host}',
  'guide.hostPhotoAltNoName': 'Your host',
  'guide.yourHost': 'your host',
  'guide.stayCheckIn': 'Check-in',
  'guide.stayCheckOut': 'Check-out',
  'guide.dateAtTime': '{date} at {time}',
  'guide.callHost': 'Call {host}',
  'guide.cardArrival': 'Arrival',
  'guide.cardArrivalSub': 'Directions, parking, entrance and keys',
  'guide.cardEssentials': 'Essentials',
  'guide.cardEssentialsSub': 'Wi-Fi, how things work, answers',
  'guide.cardLocal': 'Local tips',
  'guide.cardLocalSub': 'Places your host recommends',
  'guide.cardCheckout': 'Check-out',
  'guide.cardCheckoutSub': 'Your {time} departure checklist',
  'guide.cardPhotos': 'Photos',
  'guide.now': 'Now',
  // The access notice (hub, Arrival, Essentials while the codes are held back).
  'guide.noticeIncomplete': 'Finish your check-in to unlock door codes and Wi-Fi.',
  'guide.continueCheckin': 'Continue check-in',
  'guide.noticeNotYet': 'Door codes and Wi-Fi appear at {time} on {date}.',
  'guide.noticeSuspended': 'Door codes are not available right now. Contact your host.',
  'guide.noticeIdReview': 'Your host is confirming an ID. Door codes and Wi-Fi appear as soon as they have.',
  // IS9: a hotel stay that has no room yet (reception gives it one at arrival).
  'guide.noticeRoomPending': 'Your room is being prepared. Your room number and door details appear here as soon as reception gives you your room.',
  // Arrival.
  'guide.arrivalTitle': 'Arrival, step by step',
  'guide.step1': 'Find the building',
  'guide.step2': 'Park',
  'guide.step3': 'Enter the building',
  'guide.step4': 'Find your door',
  'guide.step5': 'Collect the key',
  'guide.step6': 'Get connected',
  'guide.openInMaps': 'Open in Maps',
  'guide.contactHost': 'Contact your host for this detail',
  'guide.doorLine': 'Floor {floor} · Door {door}',
  'guide.floorOnly': 'Floor {floor}',
  'guide.doorOnly': 'Door {door}',
  'guide.parkingSpaceLabel': 'Parking space',
  'guide.buildingCodeLabel': 'Building code',
  'guide.lockboxCodeLabel': 'Lockbox code',
  'guide.wifiNetwork': 'Network',
  'guide.wifiPassword': 'Password',
  'guide.copyBuildingCode': 'Copy building code',
  'guide.buildingCodeCopied': 'Building code copied',
  'guide.copyLockboxCode': 'Copy lockbox code',
  'guide.lockboxCodeCopied': 'Lockbox code copied',
  'guide.copyWifiName': 'Copy Wi-Fi name',
  'guide.wifiNameCopied': 'Wi-Fi name copied',
  'guide.copyWifiPassword': 'Copy Wi-Fi password',
  'guide.wifiPasswordCopied': 'Wi-Fi password copied',
  // Essentials.
  'guide.essentialsTitle': 'Essentials',
  'guide.tileWifi': 'Wi-Fi',
  'guide.wifiLater': 'Available when your door codes unlock',
  'guide.tileHost': 'Your host',
  'guide.tileQuiet': 'Quiet hours',
  'guide.airConditioning': 'Air conditioning',
  'guide.heating': 'Heating',
  'guide.hotWater': 'Hot water',
  'guide.rubbish': 'Rubbish & recycling',
  'guide.goodToKnow': 'Good to know',
  'guide.houseRules': 'House rules you signed',
  // Local tips.
  'guide.localTitle': 'Local tips',
  'guide.kind_eat': 'Eat & drink',
  'guide.kind_see': 'See & do',
  'guide.kind_nearby': 'Nearby',
  'guide.kind_transport': 'Getting around',
  'guide.kind_other': 'More',
  'guide.localEmpty': 'Your host has not added local tips yet.',
  // Check-out.
  'guide.checkoutTitle': 'Check-out by {time}',
  'guide.checklistTitle': 'Before you leave',
  // Photos.
  'guide.photosTitle': 'Photos',
  'guide.photosEmpty': 'Your host has not added photos yet.',
  'guide.openPhoto': 'Open photo: {title}',
  'guide.openPhotoN': 'Open photo {n}',
  'guide.photoCount': 'Photo {n} of {total}',
  'guide.sharePhoto': 'Share photo',
  'guide.photoSaved': 'Photo saved to your device',
  'guide.photoNotSaved': 'We could not save the photo. Try again.',
  'guide.shareInPreview': "Sharing works on the guest's page.",
  // The help strip (every guide page).
  'guide.helpTitle': 'Need help?',
  'guide.helpBody': 'For questions about your stay, contact {host}.',
  'guide.helpEmergency': 'For police, fire or medical emergencies, call {number}.',
  'guide.callHostButton': 'Call host',
  'guide.emergencyButton': 'Emergency {number}',
  // S6's done screen (the [data-arrival-slot]).
  'guide.openGuide': 'Open your stay guide',
  // The host preview (S7.9).
  'guide.previewRibbon': 'Host preview — not a guest link. Guests see this only through their own check-in link.',
  'guide.previewShowAs': 'Show the guide as',
  'guide.previewBeforeReveal': 'Before door codes',
  'guide.previewReleased': 'Door codes visible',
  'guide.previewDuring': 'During the stay',
  'guide.previewCheckout': 'Check-out day',
  'guide.previewError': 'We could not load the preview.',
};

export type StayKey = keyof typeof EN;

export const STAY_STRINGS: Record<StayLocale, Record<StayKey, string>> = { en: EN };

/** The words for `key`, with every {var} replaced. An unknown var is left as written. */
export const t = (locale: StayLocale, key: StayKey, vars?: Record<string, string | number>): string => {
  const words = (STAY_STRINGS[locale] || STAY_STRINGS.en)[key] || STAY_STRINGS.en[key] || '';
  if (!vars) return words;

  return words.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole
  );
};

export const STAY_ERROR_STRINGS = {
  STAY_LINK_NOT_VALID: 'This check-in link is not valid. Please contact your host.',
  RATE_LIMITED: 'Too many attempts. Please wait a minute and try again.',
  CONSENT_REQUIRED: 'Please agree to the identity check first.',
  CONSENT_OUTDATED: 'The privacy notice has changed. Please read it again.',
  DESK_SHARE_NOT_ASKED: 'This is no longer needed. Please reload the page.',
  TRIP_NOT_CONFIRMED: 'Please confirm your dates first.',
  ADULTS_NOT_DECLARED: 'Please say how many adults are coming first.',
  PARTY_FULL: 'Everyone in your group is already named.',
  PARTY_NOT_NEEDED: 'Only the lead guest gives their ID for this stay.',
  GUEST_LOCKED: 'This person has already started their check-in.',
  ALREADY_VERIFIED: 'Your ID is already done.',
  IDENTITY_IN_PROGRESS: 'Your ID check is still being processed. Please wait a moment.',
  IDENTITY_SELFIE_CONSENT: 'Please agree to the selfie first, or verify without one.',
  IDENTITY_ATTEMPTS_EXHAUSTED: 'Please contact your host to finish check-in.',
  IDENTITY_UNAVAILABLE: "We couldn't open the ID check. Try again in a minute.",
  ID_SCAN_LIMIT: 'Too many photos for now. Please enter the details yourself.',
  ID_SCAN_BAD_IMAGE: 'Please send a photo (JPEG, PNG or HEIC) under 6 MB.',
  ID_DETAILS_INVALID: 'Please check the highlighted details.',
  ID_PROOF_INVALID: 'That photo has expired. Please take it again, or enter the details yourself.',
  STAY_GUEST_ERROR: 'Something went wrong. Please try again.',
  CHECKIN_STALE: 'Your check-in changed in another window. We reloaded it.',
  CHECKIN_COMPLETED: 'Your check-in is complete, so this cannot be changed now.',
  STEP_LOCKED: 'Please finish the earlier steps first.',
  IDENTITY_NOT_ONLINE: 'This person does not give an ID online.',
  RULES_ALREADY_SIGNED: 'The house rules are already signed.',
  RULES_CHANGED: 'Your host updated the house rules. Please read them again.',
  EVENT_TOO_EARLY: 'This is available from your check-in date.',
  HELP_LIMIT: 'You have sent the most requests for today. Please call your host.',
  PDF_BUSY: 'Busy for a moment. Please try again.',
  ORG_SUSPENDED_PUBLIC: 'Check-in is paused by the host for now.',
  PIN_FORMAT: 'Enter the five-digit PIN from your host.',
  PIN_WRONG: 'That PIN is not right.',
  PIN_LOCKED: 'Too many wrong PINs. Try again later.',
};

export type StayErrorCode = keyof typeof STAY_ERROR_STRINGS;

export const isStayErrorCode = (code: unknown): code is StayErrorCode =>
  typeof code === 'string' && Object.prototype.hasOwnProperty.call(STAY_ERROR_STRINGS, code);

/** The words for a server refusal — ours, never the server's raw text. */
export const errorWords = (code: unknown): string =>
  isStayErrorCode(code) ? STAY_ERROR_STRINGS[code] : STAY_ERROR_STRINGS.STAY_GUEST_ERROR;
